import React, { useEffect, useState } from 'react';
import TextInput from '../../widgets/TextInput';
import CheckboxInput from '../../widgets/CheckboxInput';
import { Supplier } from '../../../models/Supplier';
import { FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import ContactsTab from '../Contacts/ContactsTab';
import GenericVerticalTabs, { TabConfig } from '../../common/GenericVerticalTabs/GenericVerticalTabs';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import './SupplierDialog.scss';
import { useTranslation } from 'react-i18next';

interface SupplierDialogProps {
  open: boolean;
  supplier: Supplier | null;
  onSave: (supplier: Supplier) => Promise<Supplier>;
  onDelete: (supplier: Supplier) => void;
  onClose: () => void;
}

const emptySupplier: Supplier = {
  id: '',
  name: '',
  isCompany: false,
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipCode: '',
  provinceOrState: '',
  country: '',
  phoneNumber: '',
  mobileNumber: '',
  emailAddress: '',
  btwID: '',
  kvkID: '',
  bankAccountNumber: '',
};

const SupplierDialog: React.FC<SupplierDialogProps> = ({ open, supplier, onSave, onDelete, onClose }) => {
  const { t } = useTranslation(['common', 'screens']);
  const [data, setData] = useState<Supplier>(supplier ?? emptySupplier);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'contacts'>('details');

  useEffect(() => {
    setData(supplier ?? { ...emptySupplier });
    setShowDeleteConfirm(false);
    setActiveTab('details');
  }, [supplier, open]);

  if (!open) return null;

  const isNew = !supplier?.id;
  const set = <K extends keyof Supplier>(field: K, value: Supplier[K]) =>
    setData(prev => ({ ...prev, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const saved = await onSave(data);
      setData(saved);
    } catch {
      // parent handles error display
    }
  };

  const tabs: TabConfig[] = [
    {
      key: 'details',
      label: t('screens:supplierDialog.tabs.details'),
      content: (
        <div className="supplier-dialog__details-form">
          <div className="supplier-dialog__details-scroll">
            {/* Section: General */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:supplierDialog.sections.general')}</div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.name')} value={data.name ?? ''} required onChange={v => set('name', v)} />
                </div>
                <div className="supplier-dialog__col--checkbox">
                  <CheckboxInput label={t('screens:supplierDialog.fields.isCompany')} checked={data.isCompany} onChange={v => set('isCompany', v)} />
                </div>
              </div>
            </div>

            {/* Section: Address */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:supplierDialog.sections.address')}</div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.addressLine1')} value={data.addressLine1 ?? ''} onChange={v => set('addressLine1', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.addressLine2')} value={data.addressLine2 ?? ''} onChange={v => set('addressLine2', v)} />
                </div>
              </div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.city')} value={data.city ?? ''} onChange={v => set('city', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.zipCode')} value={data.zipCode ?? ''} onChange={v => set('zipCode', v)} />
                </div>
              </div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.provinceState')} value={data.provinceOrState ?? ''} onChange={v => set('provinceOrState', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.country')} value={data.country ?? ''} onChange={v => set('country', v)} />
                </div>
              </div>
            </div>

            {/* Section: Contact */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:supplierDialog.sections.contact')}</div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.phoneNumber')} value={data.phoneNumber ?? ''} onChange={v => set('phoneNumber', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.mobileNumber')} value={data.mobileNumber ?? ''} onChange={v => set('mobileNumber', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.emailAddress')} value={data.emailAddress ?? ''} onChange={v => set('emailAddress', v)} />
                </div>
              </div>
            </div>

            {/* Section: Financial */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:supplierDialog.sections.financial')}</div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.btwVatId')} value={data.btwID ?? ''} onChange={v => set('btwID', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:supplierDialog.fields.kvkNumber')} value={data.kvkID ?? ''} onChange={v => set('kvkID', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:supplierDialog.fields.bankAccountNumber')} value={data.bankAccountNumber ?? ''} onChange={v => set('bankAccountNumber', v)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    ...(!isNew ? [{ key: 'contacts', label: t('screens:supplierDialog.tabs.contacts'), content: <ContactsTab parentId={data.id} parentType="Supplier" /> }] : []),
  ];

  return (
    <ModalDialog
      open={open}
      title={isNew ? t('screens:supplierDialog.newTitle') : t('screens:supplierDialog.editTitle')}
      subtitle={!isNew ? supplier?.name : undefined}
      onClose={onClose}
      onSubmit={handleSave}
      width={1024}
      minHeight={1024}
      bodyVariant="tabs"
      footerLeft={!isNew ? (
        showDeleteConfirm ? (
          <div className="supplier-dialog__delete-confirm">
            <span className="supplier-dialog__delete-confirm-text">{t('common:areYouSure')}</span>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(data)}>
              <FaTrash style={{ marginRight: 5 }} />{t('common:yesDelete')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
              {t('common:cancel')}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
            <FaTrash style={{ marginRight: 5 }} />{t('common:delete')}
          </button>
        )
      ) : undefined}
      footerRight={
        <>
          <button type="submit" className="btn btn-primary">
            <FaSave style={{ marginRight: 5 }} />{t('common:save')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <FaTimes style={{ marginRight: 5 }} />{t('common:close')}
          </button>
        </>
      }
    >
      <GenericVerticalTabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={tab => setActiveTab(tab as 'details' | 'contacts')}
        className="supplier-dialog__tabs"
      />
    </ModalDialog>
  );
};

export default SupplierDialog;
